/**
 *
 * This method is intended for encoding *key* or *value* parts of query component. We need a custom
 * method because encodeURIComponent is too aggressive and encodes stuff that doesn't have to be
 * encoded per http://tools.ietf.org/html/rfc3986:
 *    query       = *( pchar / "/" / "?" )
 *    pchar         = unreserved / pct-encoded / sub-delims / ":" / "@"
 *    unreserved    = ALPHA / DIGIT / "-" / "." / "_" / "~"
 *    pct-encoded   = "%" HEXDIG HEXDIG
 *    sub-delims    = "!" / "$" / "&" / "'" / "(" / ")"
 *                     / "*" / "+" / "," / ";" / "="
 */
function encodeUriQuery(val: string, pctEncodeSpaces?: boolean): string {
  return encodeURIComponent(val)
    .replace(/%40/gi, "@")
    .replace(/%3A/gi, ":")
    .replace(/%24/g, "$")
    .replace(/%2C/gi, ",")
    .replace(/%3B/gi, ";")
    .replace(/%20/g, pctEncodeSpaces ? "%20" : "+");
}

/**
 * Determines if a value is a date.
 *
 * @param value Reference to check.
 * @returns True if `value` is a `Date`.
 */
function isDate(value: unknown): boolean {
  return Object.prototype.toString.call(value) === "[object Date]";
}

export { isDate };

function toJsonReplacer(key: string, value: unknown): unknown {
  let val = value;

  // Remove private variables
  if (
    typeof key === "string" &&
    key.charAt(0) === "$" &&
    key.charAt(1) === "$"
  ) {
    val = undefined;
  }

  return val;
}

function toJson(obj: unknown): string | undefined {
  if (!obj) {
    return undefined;
  }

  return JSON.stringify(obj, toJsonReplacer);
}

function serializeValue(v: unknown): string {
  if (!v) return "";

  if (isDate(v)) {
    return (v as Date).toISOString();
  }
  if (typeof v === "object") {
    return toJson(v) || "";
  }

  return String(v);
}

/**
 * Converts object into URL parameters.
 * @param params parameter object
 * @return url string
 */
function queryParamSerializer(params: Record<string, unknown>): string {
  if (!params) {
    return "";
  }

  const parts: string[] = [];

  Object.keys(params)
    .sort()
    .forEach((key) => {
      const value = params[key];

      if (!value) {
        return;
      }

      if (Array.isArray(value)) {
        value.forEach((v: unknown) => {
          parts.push(
            `${encodeUriQuery(key)}=${encodeUriQuery(serializeValue(v))}`
          );
        });
      } else {
        parts.push(
          `${encodeUriQuery(key)}=${encodeUriQuery(serializeValue(value))}`
        );
      }
    });

  return parts.join("&");
}

export { queryParamSerializer };
